import type { WritableSignal } from '@angular/core';
import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import type { AppIdentity } from '@evc/platform';
import type { Menu, MenuItemType } from '@evc/web-components';
import { BtnActionEnum } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';
import { VERSION } from '@shared/version/version';

@Injectable({
  providedIn: 'root',
})
export class NavConfigService {
  readonly #router = inject(Router);

  #leftbarConfig!: WritableSignal<Menu[]>;
  #helpMenu!: WritableSignal<MenuItemType[]>;
  #appIdentity!: WritableSignal<AppIdentity>;
  #i18nService = inject(I18nService);

  constructor() {
    this.#initConfig();

    this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateSelectedMenuItem();
      }
    });
  }

  #initConfig(): void {
    this.#leftbarConfig = signal<Menu[]>([
      {
        items: [
          {
            text: this.#i18nService.t$('PAGE_TEAM.title'),
            key: '/',
            icon: 'users',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/'),
            },
          },
          {
            text: this.#i18nService.t$('PAGE_APPLICATIONS.title'),
            key: '/licenses',
            icon: 'global-menu',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/licenses'),
            },
          },
          {
            text: this.#i18nService.t$('PAGE_LINKED_ORGS.title'),
            key: '/linked-organizations',
            icon: 'share',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/linked-organizations'),
            },
          },
        ],
      },
      {
        items: [
          {
            text: this.#i18nService.t$('PAGE_SETTINGS.title'),
            key: '/organization-settings',
            icon: 'settings-alt',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/organization-settings'),
            },
          },
        ],
      },
    ]);

    this.#appIdentity = signal<AppIdentity>({
      title: this.#i18nService.t$('title'),
      logo: '/assets/applications/organization.svg#organization',
      version: VERSION.semver,
    });

    this.#helpMenu = signal<MenuItemType[]>(
      ['help_and_support', 'release_notes', 'terms_of_use', 'platform_status'].map((key) => ({
        key,
        text: this.#i18nService.t$(`links.${key}.text`),
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: this.#i18nService.t$(`links.${key}.href`),
        },
      })) as MenuItemType[],
    );
  }

  updateSelectedMenuItem(): void {
    if (!this.#leftbarConfig) return;

    const currentUrl = this.#router.url;

    this.#leftbarConfig.update((config) =>
      config.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          selected: item.key === currentUrl,
        })),
      })),
    );
  }

  get leftbarConfig(): WritableSignal<Menu[]> {
    return this.#leftbarConfig;
  }

  set leftbarConfig(value: Menu[]) {
    this.#leftbarConfig.set(value);
  }

  get appIdentity(): WritableSignal<AppIdentity> {
    return this.#appIdentity;
  }

  get helpMenu(): WritableSignal<MenuItemType[]> {
    return this.#helpMenu;
  }
}
